import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useColorModeValue } from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { useToast } from '@/client/hooks/useToast';
import { useProfilingConfig, useUpdateProfilingConfig } from '@/lib/sdk/profiling/queries';
import type { ProfilingConfig } from '@/lib/sdk/profiling/types';

interface SettingsFormData {
  enabled: boolean;
  scheduled_enabled: boolean;
  schedule_cron: string;
  cpu_sample_rate_hz: number;
  retention_days: number;
  auto_analyze: boolean;
  alert_goroutine_count: number;
  alert_heap_alloc_mb: number;
  alert_cpu_percent: number;
}

export { Controller };

export function useProfilingSettingsPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  const { data: config, isLoading } = useProfilingConfig();
  const { mutate: updateConfig, isPending: isUpdating } = useUpdateProfilingConfig();

  const cardGradient = useColorModeValue(
    'linear(to-br, white, brand.50)',
    'linear(to-br, gray.800, gray.700)'
  );

  const { register, handleSubmit: rhfHandleSubmit, control, reset, formState: { errors, isDirty } } = useForm<SettingsFormData>({
    defaultValues: {
      enabled: false, scheduled_enabled: false, schedule_cron: '0 */4 * * *',
      cpu_sample_rate_hz: 100, retention_days: 30, auto_analyze: true,
      alert_goroutine_count: 10000, alert_heap_alloc_mb: 1024, alert_cpu_percent: 80,
    },
  });

  useEffect(() => {
    if (config) {
      reset({
        enabled: config.enabled,
        scheduled_enabled: config.scheduled_enabled,
        schedule_cron: config.schedule_cron,
        cpu_sample_rate_hz: config.cpu_sample_rate_hz,
        retention_days: config.retention_days,
        auto_analyze: config.auto_analyze,
        alert_goroutine_count: config.alert_thresholds.goroutine_count,
        alert_heap_alloc_mb: config.alert_thresholds.heap_alloc_mb,
        alert_cpu_percent: config.alert_thresholds.cpu_percent,
      });
    }
  }, [config, reset]);

  const isSuperAdmin = user?.is_super_admin === true;

  const onSubmit = (data: SettingsFormData) => {
    const configUpdate: Partial<ProfilingConfig> = {
      enabled: data.enabled,
      scheduled_enabled: data.scheduled_enabled,
      schedule_cron: data.schedule_cron,
      cpu_sample_rate_hz: data.cpu_sample_rate_hz,
      retention_days: data.retention_days,
      auto_analyze: data.auto_analyze,
      alert_thresholds: {
        goroutine_count: data.alert_goroutine_count,
        heap_alloc_mb: data.alert_heap_alloc_mb,
        cpu_percent: data.alert_cpu_percent,
      },
    };
    updateConfig(configUpdate, {
      onSuccess: () => toast.success('Settings saved successfully'),
      onError: (error: Error) => toast.error(error.message || 'Failed to save settings'),
    });
  };

  const onSettingsSubmit = rhfHandleSubmit(onSubmit);

  return {
    orgId, navigate, isSuperAdmin,
    isLoading, isUpdating, isDirty,
    cardGradient,
    register, control, errors,
    onSettingsSubmit,
  };
}
